import { GPXData, GPXWaypoint, Coordinate } from '../types'

// Smooth elevation data using a simple moving average to reduce GPS noise
function smoothElevations(
  points: Coordinate[],
  windowSize: number = 5,
): number[] {
  const smoothed: number[] = []
  const half = Math.floor(windowSize / 2)
  for (let i = 0; i < points.length; i++) {
    const start = Math.max(0, i - half)
    const end = Math.min(points.length - 1, i + half)
    let sum = 0
    let count = 0
    for (let j = start; j <= end; j++) {
      sum += points[j].ele
      count++
    }
    smoothed.push(sum / count)
  }
  return smoothed
}

// Calculate elevation gain/loss with a minimum threshold to filter GPS noise
function calculateElevationWithThreshold(
  elevations: number[],
  threshold: number = 3,
): { gain: number; loss: number } {
  let gain = 0
  let loss = 0
  let lastSignificant = elevations[0]

  for (let i = 1; i < elevations.length; i++) {
    const diff = elevations[i] - lastSignificant
    if (Math.abs(diff) >= threshold) {
      if (diff > 0) {
        gain += diff
      } else {
        loss += Math.abs(diff)
      }
      lastSignificant = elevations[i]
    }
  }

  return { gain, loss }
}

export const parseGpx = (gpxString: string): GPXData => {
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(gpxString, 'text/xml')

  const trkpts = xmlDoc.getElementsByTagName('trkpt')
  const points: Coordinate[] = []

  let totalDistance = 0

  for (let i = 0; i < trkpts.length; i++) {
    const pt = trkpts[i]
    const lat = parseFloat(pt.getAttribute('lat') || '0')
    const lon = parseFloat(pt.getAttribute('lon') || '0')
    const ele = parseFloat(
      pt.getElementsByTagName('ele')[0]?.textContent || '0',
    )
    const time = pt.getElementsByTagName('time')[0]?.textContent || undefined

    let distFromStart = 0

    if (i > 0) {
      const prev = points[i - 1]
      const dist = haversineDistance(prev.lat, prev.lon, lat, lon)
      totalDistance += dist
      distFromStart = totalDistance
    }

    points.push({ lat, lon, ele, time, distFromStart })
  }

  // Smooth elevations and calculate gain/loss with noise threshold
  const smoothed = smoothElevations(points, 7)
  const { gain: totalElevationGain, loss: totalElevationLoss } =
    calculateElevationWithThreshold(smoothed, 3)

  const name =
    xmlDoc.getElementsByTagName('name')[0]?.textContent || 'Imported Track'

  // Parse waypoints (<wpt> elements)
  const waypoints = parseWaypoints(xmlDoc)

  return {
    name,
    points,
    totalDistance,
    totalElevationGain,
    totalElevationLoss,
    waypoints: waypoints.length > 0 ? waypoints : undefined,
  }
}

function parseWaypoints(xmlDoc: Document): GPXWaypoint[] {
  const wptElements = xmlDoc.getElementsByTagName('wpt')
  const waypoints: GPXWaypoint[] = []

  for (let i = 0; i < wptElements.length; i++) {
    const wpt = wptElements[i]
    const lat = parseFloat(wpt.getAttribute('lat') || '0')
    const lon = parseFloat(wpt.getAttribute('lon') || '0')
    const ele = parseFloat(
      wpt.getElementsByTagName('ele')[0]?.textContent || '0',
    )
    const name =
      wpt.getElementsByTagName('name')[0]?.textContent || `WPT ${i + 1}`
    const desc = wpt.getElementsByTagName('desc')[0]?.textContent || undefined
    const type = wpt.getElementsByTagName('type')[0]?.textContent || undefined

    waypoints.push({ name, lat, lon, ele, desc, type })
  }

  return waypoints
}

/**
 * Find the index of the closest track point to a given lat/lon coordinate.
 */
export function findClosestPointIndex(
  points: Coordinate[],
  lat: number,
  lon: number,
): number {
  let closestIndex = 0
  let minDist = Infinity

  for (let i = 0; i < points.length; i++) {
    const d = haversineDistance(points[i].lat, points[i].lon, lat, lon)
    if (d < minDist) {
      minDist = d
      closestIndex = i
    }
  }

  return closestIndex
}

function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371
  const dLat = deg2rad(lat2 - lat1)
  const dLon = deg2rad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

function deg2rad(deg: number): number {
  return deg * (Math.PI / 180)
}

export const calculateSegmentStats = (
  points: Coordinate[],
  startIndex: number,
  endIndex: number,
) => {
  let distance = 0

  const start = Math.max(0, startIndex)
  const end = Math.min(points.length - 1, endIndex)

  if (start >= end)
    return { distance: 0, elevationGain: 0, elevationLoss: 0, avgGrade: 0 }

  if (
    points[end].distFromStart !== undefined &&
    points[start].distFromStart !== undefined
  ) {
    distance = points[end].distFromStart! - points[start].distFromStart!
  } else {
    for (let i = start + 1; i <= end; i++) {
      distance += haversineDistance(
        points[i - 1].lat,
        points[i - 1].lon,
        points[i].lat,
        points[i].lon,
      )
    }
  }

  // Use same smoothing window (7) and threshold (3) as parseGpx for consistency
  const segmentPoints = points.slice(start, end + 1)
  const smoothed = smoothElevations(segmentPoints, 7)
  const { gain, loss } = calculateElevationWithThreshold(smoothed, 3)

  const avgGrade = distance > 0 ? ((gain - loss) / (distance * 1000)) * 100 : 0

  return { distance, elevationGain: gain, elevationLoss: loss, avgGrade }
}
